import { Search, SlidersHorizontal, X } from "lucide-react";
import type { ChangeEvent, JSX, KeyboardEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Select } from "@/shared/ui/select";

import { useOrderMarketplaces } from "../api/use-order-marketplaces";
import type { OrderFilters as OrderFiltersState } from "../api/use-orders";
import { MARKETPLACE_LABEL } from "../lib/marketplace-labels";
import {
  MARKETPLACE_STATUS_CODES,
  MARKETPLACE_STATUS_LABEL,
} from "../lib/marketplace-status-labels";
import {
  countDrawerFilters,
  hasActiveFilters,
  joinTokens,
  searchTokens,
} from "../lib/order-url-state";
import type { FilterUpdate } from "../lib/order-url-state";

interface OrderFiltersProps {
  filter: OrderFiltersState;
  onChange: (updates: FilterUpdate) => void;
  onClear: () => void;
  onOpenFilters: () => void;
}

const FALLBACK_MARKETPLACES = ["shopee", "lazada", "tiktok"] as const;

function mkpLabel(value: string): string {
  const known = MARKETPLACE_LABEL[value as keyof typeof MARKETPLACE_LABEL];
  if (known !== undefined) return known;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Returns a local YYYY-MM-DD string (NOT UTC) to avoid off-by-one in Asia/Bangkok. */
function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function OrderFilters({
  filter,
  onChange,
  onClear,
  onOpenFilters,
}: OrderFiltersProps): JSX.Element {
  const { t } = useTranslation("orders");
  const [draft, setDraft] = useState("");
  const { data: marketplacesData } = useOrderMarketplaces();
  const marketplaces: string[] =
    marketplacesData && marketplacesData.length > 0
      ? marketplacesData.map((m) => m.value)
      : [...FALLBACK_MARKETPLACES];

  const drawerCount = countDrawerFilters(filter);

  const mkpStatusKey =
    filter.marketplace && filter.marketplace_status
      ? `${filter.marketplace}:${filter.marketplace_status}`
      : "all";

  function handleMkpChange(e: ChangeEvent<HTMLSelectElement>): void {
    const v = e.target.value;
    onChange(
      v === "all"
        ? { marketplace: undefined, marketplace_status: undefined }
        : { marketplace: v, marketplace_status: undefined },
    );
  }

  function handleMkpStatusChange(e: ChangeEvent<HTMLSelectElement>): void {
    const v = e.target.value;
    if (v === "all") {
      onChange({ marketplace_status: undefined, offset: 0 });
      return;
    }
    // Picking a status also picks its marketplace — the most-used filter on
    // this page, so it must not require a marketplace to be chosen first.
    const [mkp, code] = v.split(":") as [string, string];
    onChange({ marketplace: mkp, marketplace_status: code, offset: 0 });
  }

  function handleDraftChange(e: ChangeEvent<HTMLInputElement>): void {
    setDraft(e.target.value);
  }

  function commitSearch(): void {
    const next = joinTokens([...searchTokens(filter.search ?? ""), ...searchTokens(draft)]);
    if (next !== (filter.search ?? "")) onChange({ search: next || undefined, offset: 0 });
    setDraft("");
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSearch();
    }
  }

  function handleExcToggle(): void {
    // When turning OFF, emit undefined so mergeFilter removes the key and
    // the URL never carries ?exception_only=false.
    onChange({ exception_only: filter.exception_only ? undefined : true });
  }

  function handleDateFromChange(e: ChangeEvent<HTMLInputElement>): void {
    const v = e.target.value;
    onChange(v ? { date_from: v, offset: 0 } : { date_from: undefined, offset: 0 });
  }

  function handleDateToChange(e: ChangeEvent<HTMLInputElement>): void {
    const v = e.target.value;
    onChange(v ? { date_to: v, offset: 0 } : { date_to: undefined, offset: 0 });
  }

  function handlePresetToday(): void {
    const today = toLocalYmd(new Date());
    onChange({ date_from: today, date_to: today, offset: 0 });
  }

  function handlePreset7d(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    onChange({ date_from: toLocalYmd(from), date_to: toLocalYmd(today), offset: 0 });
  }

  function handlePresetMonth(): void {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    onChange({ date_from: toLocalYmd(firstOfMonth), date_to: toLocalYmd(today), offset: 0 });
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="order-filters">
      {/* Search */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
          aria-hidden
        />
        <input
          type="search"
          placeholder={t("search_orders")}
          value={draft}
          onChange={handleDraftChange}
          onKeyDown={handleSearchKeyDown}
          onBlur={commitSearch}
          className="h-9 rounded-[10px] border border-[var(--premium-border-strong)] bg-white pl-8 pr-3 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          data-testid="filter-search"
        />
      </div>

      {/* Marketplace */}
      <Select
        value={filter.marketplace ?? "all"}
        onChange={handleMkpChange}
        data-testid="filter-marketplace"
        aria-label={t("f_all_mkp")}
      >
        <option value="all">{t("f_all_mkp")}</option>
        {marketplaces.map((m) => (
          <option key={m} value={m}>
            {mkpLabel(m)}
          </option>
        ))}
      </Select>

      {/* Marketplace status — grouped by marketplace; selecting one sets both */}
      <Select
        value={mkpStatusKey}
        onChange={handleMkpStatusChange}
        data-testid="filter-marketplace-status"
        aria-label={t("f_all_mkp_status")}
      >
        <option value="all">{t("f_all_mkp_status")}</option>
        {(Object.entries(MARKETPLACE_STATUS_CODES) as [string, string[]][]).map(([mkp, codes]) => (
          <optgroup key={mkp} label={mkpLabel(mkp)}>
            {codes.map((code) => (
              <option key={`${mkp}:${code}`} value={`${mkp}:${code}`}>
                {MARKETPLACE_STATUS_LABEL[code] ?? code}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>

      {/* Date range inputs */}
      <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
        {t("f_date_from")}
        <input
          type="date"
          value={filter.date_from ?? ""}
          onChange={handleDateFromChange}
          data-testid="filter-date-from"
          aria-label={t("f_date_from")}
          className="h-9 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-2 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
        />
      </label>
      <label className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
        {t("f_date_to")}
        <input
          type="date"
          value={filter.date_to ?? ""}
          onChange={handleDateToChange}
          data-testid="filter-date-to"
          aria-label={t("f_date_to")}
          className="h-9 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-2 text-sm text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
        />
      </label>

      {/* Date presets */}
      <button
        type="button"
        onClick={handlePresetToday}
        data-testid="filter-date-today"
        className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
      >
        {t("f_date_today")}
      </button>
      <button
        type="button"
        onClick={handlePreset7d}
        data-testid="filter-date-7d"
        className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
      >
        {t("f_date_7d")}
      </button>
      <button
        type="button"
        onClick={handlePresetMonth}
        data-testid="filter-date-month"
        className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
      >
        {t("f_date_month")}
      </button>

      {/* Exceptions toggle */}
      <button
        type="button"
        aria-pressed={!!filter.exception_only}
        onClick={handleExcToggle}
        data-testid="filter-exceptions"
        className={[
          "inline-flex h-9 items-center gap-1.5 rounded-[10px] border px-3 text-sm font-semibold transition focus-ring",
          filter.exception_only
            ? "border-[var(--intent-danger-fg)] bg-[var(--intent-danger-bg)] text-[var(--intent-danger-fg)]"
            : "border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand-500)]",
        ].join(" ")}
      >
        {t("exc_filter")}
      </button>

      {/* Filters button (opens the drawer) */}
      <button
        type="button"
        onClick={onOpenFilters}
        data-testid="filter-open-drawer"
        className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        {t("btn_filters")}
        {drawerCount > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--brand-500)] px-1.5 text-[11px] font-bold text-white">
            {drawerCount}
          </span>
        )}
      </button>

      {/* Clear button */}
      {hasActiveFilters(filter) && (
        <button
          type="button"
          onClick={onClear}
          data-testid="filter-clear"
          className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--premium-border-strong)] bg-white px-3 text-sm font-semibold text-[var(--text-secondary)] transition hover:border-[var(--brand-500)] focus-ring"
        >
          <X className="h-4 w-4" aria-hidden />
          {t("btn_clear_filters")}
        </button>
      )}
    </div>
  );
}
